import React from 'react';
import { Box } from '@mui/material';
import { useMidiContext } from 'Contexts/MidiContext';
import { isCapacitorNative } from 'Utils/Constants';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { useAudioRecognition } from './AudioRecognitionContext';
import type { RecognitionFrame } from './types';
import { hzToName } from './util/note';

const WAVEFORM_WIDTH = 272;
const WAVEFORM_HEIGHT = 72;
const SPECTRUM_WIDTH = 272;
const SPECTRUM_HEIGHT = 146;
const SPECTRUM_MIN_HZ = 50;
const SPECTRUM_MAX_HZ = 2000;
const OVERLAY_FRAME_PUBLISH_MS = 120;

const formatNumber = (value: number | null | undefined, digits = 3) =>
  value == null || !Number.isFinite(value) ? '--' : value.toFixed(digits);

const formatHz = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value) ? '--' : `${value.toFixed(1)} Hz`;

const formatAmp = (value: number | null | undefined, digits = 4) =>
  value == null || !Number.isFinite(value) ? '--' : value.toFixed(digits);

const waveformPath = (waveform?: Float32Array) => {
  if (!waveform || waveform.length === 0) return '';
  const midY = WAVEFORM_HEIGHT / 2;
  const xScale = waveform.length <= 1 ? 0 : WAVEFORM_WIDTH / (waveform.length - 1);
  const yScale = midY - 4;
  let path = '';

  for (let i = 0; i < waveform.length; i++) {
    const x = i * xScale;
    const y = midY - waveform[i] * yScale;
    path += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
  }

  return path.trim();
};

const peakWidth = (amp: number, strongestAmp: number) => {
  if (!Number.isFinite(amp) || !Number.isFinite(strongestAmp) || strongestAmp <= 0) return '0%';
  return `${Math.max(8, Math.min(100, (amp / strongestAmp) * 100)).toFixed(0)}%`;
};

const peakX = (hz: number, minHz: number, maxHz: number) => {
  const clampedHz = Math.min(maxHz, Math.max(minHz, hz));
  const ratio = (clampedHz - minHz) / Math.max(1, maxHz - minHz);
  return 28 + ratio * (SPECTRUM_WIDTH - 44);
};

const peakY = (amp: number, strongestAmp: number) => {
  if (!Number.isFinite(amp) || !Number.isFinite(strongestAmp) || strongestAmp <= 0) {
    return SPECTRUM_HEIGHT - 22;
  }
  const normalized = Math.max(0, Math.min(1, amp / strongestAmp));
  const boosted = Math.sqrt(normalized);
  return 18 + (1 - boosted) * (SPECTRUM_HEIGHT - 44);
};

const AudioRecognitionDebugOverlay: React.FC = () => {
  const enabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const { running, addListener, removeListener, calibrationProfile, calibration } =
    useAudioRecognition();
  const { audioInputLatencyMs } = useMidiContext();
  const [frame, setFrame] = React.useState<RecognitionFrame | null>(null);
  const [lastNoteNames, setLastNoteNames] = React.useState<string[]>([]);
  const latestFrameRef = React.useRef<RecognitionFrame | null>(null);
  const lastPublishedTimeSecRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled || isCapacitorNative()) return;

    const listener = {
      name: 'audio-recognition-debug-overlay',
      wantsWaveform: true,
      onFrame: (nextFrame: RecognitionFrame) => {
        latestFrameRef.current = nextFrame;
      },
      onNoteChange: (notes: string[]) => {
        setLastNoteNames(notes);
      },
    };

    addListener(listener);
    const intervalId = window.setInterval(() => {
      const nextFrame = latestFrameRef.current;
      if (!nextFrame) return;
      if (lastPublishedTimeSecRef.current === nextFrame.timeSec) return;
      lastPublishedTimeSecRef.current = nextFrame.timeSec;
      setFrame(nextFrame);
    }, OVERLAY_FRAME_PUBLISH_MS);

    return () => {
      window.clearInterval(intervalId);
      latestFrameRef.current = null;
      lastPublishedTimeSecRef.current = null;
      removeListener(listener);
    };
  }, [addListener, enabled, removeListener]);

  if (!enabled || isCapacitorNative()) return null;

  const currentNotes = lastNoteNames.length > 0 ? lastNoteNames.join(', ') : 'none';
  const profileLabel = calibrationProfile?.label ?? 'default profile';
  const templateCount = calibrationProfile?.noteTemplates.length ?? 0;
  const isAboveGate =
    frame != null &&
    Number.isFinite(frame.rms) &&
    Number.isFinite(frame.minRms) &&
    frame.rms >= frame.minRms;
  const hasStableDetection = (frame?.notes.length ?? 0) > 0 || frame?.hz != null;
  const visiblePeaks = isAboveGate || hasStableDetection ? (frame?.peaks ?? []) : [];
  const strongestAmp = visiblePeaks.reduce((max, peak) => Math.max(max, peak.amp), 0);
  const spectrumMinHz = calibrationProfile?.hpsMinHz ?? SPECTRUM_MIN_HZ;
  const spectrumMaxHz = calibrationProfile?.hpsMaxHz ?? SPECTRUM_MAX_HZ;
  const spectrumMidHz = (spectrumMinHz + spectrumMaxHz) / 2;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 88, md: 108 },
        right: { xs: 12, md: 20 },
        width: { xs: 'min(92vw, 340px)', md: '340px' },
        pointerEvents: 'none',
        zIndex: 1400,
        borderRadius: '16px',
        border: '1px solid rgba(90, 122, 180, 0.34)',
        background: 'linear-gradient(180deg, rgba(8,18,36,0.95) 0%, rgba(4,11,19,0.95) 100%)',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.35)',
        color: '#EAF2FF',
        px: 2,
        py: 1.75,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
          mb: 1.25,
        }}
      >
        <Box sx={{ fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Audio Recognition
        </Box>
        <Box
          sx={{
            fontSize: '11px',
            color: audioInputLatencyMs > 0 ? '#7DFFA5' : '#FFD27D',
          }}
        >
          {audioInputLatencyMs > 0 ? 'fallback active' : 'listening only'}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: '11px' }}>
        <Box>mic: {running ? 'running' : 'idle'}</Box>
        <Box>phase: {calibration.phase}</Box>
        <Box>notes: {currentNotes}</Box>
        <Box>profile: {profileLabel}</Box>
        <Box>templates: {templateCount}</Box>
        <Box>rms: {formatNumber(frame?.rms)}</Box>
        <Box>gate: {formatNumber(frame?.minRms)}</Box>
        <Box>noise: {formatNumber(frame?.noiseFloor)}</Box>
        <Box>primary: {formatHz(frame?.hz)}</Box>
      </Box>

      <Box sx={{ mt: 1.5, borderRadius: '10px', background: 'rgba(17, 29, 54, 0.82)', p: 1 }}>
        <svg
          width="100%"
          height={WAVEFORM_HEIGHT}
          viewBox={`0 0 ${WAVEFORM_WIDTH} ${WAVEFORM_HEIGHT}`}
        >
          <line
            x1="0"
            y1={WAVEFORM_HEIGHT / 2}
            x2={WAVEFORM_WIDTH}
            y2={WAVEFORM_HEIGHT / 2}
            stroke="rgba(155, 178, 225, 0.22)"
            strokeWidth="1"
          />
          <path
            d={waveformPath(frame?.waveform)}
            fill="none"
            stroke="#79D8FF"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>

      <Box sx={{ mt: 1.25, fontSize: '11px', color: '#B9C7E6' }}>{calibration.message}</Box>

      <Box sx={{ mt: 1.4, fontSize: '11px', color: '#DCE7FF' }}>
        {visiblePeaks.length > 0 ? 'Detected peak spectrum' : 'Peak spectrum suppressed below gate'}
      </Box>
      <Box sx={{ mt: 0.75, borderRadius: '10px', background: 'rgba(17, 29, 54, 0.82)', p: 1 }}>
        <svg
          width="100%"
          height={SPECTRUM_HEIGHT}
          viewBox={`0 0 ${SPECTRUM_WIDTH} ${SPECTRUM_HEIGHT}`}
        >
          <text x="8" y="14" fill="#9EB3DF" fontSize="10">
            amp
          </text>
          <line
            x1="28"
            y1="18"
            x2="28"
            y2={SPECTRUM_HEIGHT - 24}
            stroke="rgba(155, 178, 225, 0.28)"
            strokeWidth="1"
          />
          <line
            x1="28"
            y1={SPECTRUM_HEIGHT - 24}
            x2={SPECTRUM_WIDTH - 16}
            y2={SPECTRUM_HEIGHT - 24}
            stroke="rgba(155, 178, 225, 0.28)"
            strokeWidth="1"
          />
          <text x="28" y={SPECTRUM_HEIGHT - 8} fill="#9EB3DF" fontSize="9">
            {Math.round(spectrumMinHz)} Hz
          </text>
          <text
            x={peakX(spectrumMidHz, spectrumMinHz, spectrumMaxHz)}
            y={SPECTRUM_HEIGHT - 8}
            fill="#9EB3DF"
            fontSize="9"
            textAnchor="middle"
          >
            {Math.round(spectrumMidHz)} Hz
          </text>
          <text
            x={SPECTRUM_WIDTH - 16}
            y={SPECTRUM_HEIGHT - 8}
            fill="#9EB3DF"
            fontSize="9"
            textAnchor="end"
          >
            {Math.round(spectrumMaxHz)} Hz
          </text>
          <text x={SPECTRUM_WIDTH - 16} y="14" fill="#9EB3DF" fontSize="10" textAnchor="end">
            freq
          </text>

          {visiblePeaks.slice(0, 8).map((peak) => {
            const x = peakX(peak.hz, spectrumMinHz, spectrumMaxHz);
            const y = peakY(peak.amp, strongestAmp);
            const peakNote = hzToName(peak.hz) ?? '--';

            return (
              <g key={`spectrum-${peak.hz}-${peak.amp}`}>
                <line
                  x1={x}
                  y1={SPECTRUM_HEIGHT - 24}
                  x2={x}
                  y2={y}
                  stroke="#7DD3FC"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <circle cx={x} cy={y} r="4" fill="#4FD1C5" />
                <text x={x} y={Math.max(12, y - 8)} fill="#EAF2FF" fontSize="9" textAnchor="middle">
                  {peakNote}
                </text>
                <text
                  x={x}
                  y={Math.max(22, y - 18)}
                  fill="#B9C7E6"
                  fontSize="8"
                  textAnchor="middle"
                >
                  {peak.hz.toFixed(0)}Hz
                </text>
                <text
                  x={x}
                  y={SPECTRUM_HEIGHT - 30}
                  fill="#A8F3D0"
                  fontSize="8"
                  textAnchor="middle"
                >
                  {formatAmp(peak.amp)}
                </text>
              </g>
            );
          })}

          {visiblePeaks.length === 0 && (
            <text
              x={SPECTRUM_WIDTH / 2}
              y={SPECTRUM_HEIGHT / 2}
              fill="#9EB3DF"
              fontSize="11"
              textAnchor="middle"
            >
              Below gate: hidden
            </text>
          )}
        </svg>
      </Box>

      <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {visiblePeaks.slice(0, 6).map((peak) => {
          const peakNote = hzToName(peak.hz) ?? '--';
          return (
            <Box
              key={`${peak.hz}-${peak.amp}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ width: '72px', fontSize: '11px', color: '#DCE7FF' }}>
                {peakNote} {peak.hz.toFixed(1)}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: '8px',
                  borderRadius: '999px',
                  background: 'rgba(81, 101, 150, 0.32)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: peakWidth(peak.amp, strongestAmp),
                    height: '100%',
                    borderRadius: '999px',
                    background: 'linear-gradient(90deg, #4FD1C5 0%, #7DD3FC 100%)',
                  }}
                />
              </Box>
              <Box sx={{ width: '52px', fontSize: '10px', color: '#B9C7E6', textAlign: 'right' }}>
                {formatAmp(peak.amp)}
              </Box>
            </Box>
          );
        })}

        {visiblePeaks.length === 0 && (
          <Box sx={{ fontSize: '11px', color: '#9EB3DF' }}>
            No spectrum peaks shown while below gate.
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AudioRecognitionDebugOverlay;
